import { useMemo } from 'react';

import { Trans } from '@lingui/react/macro';
import { Button } from '@repo/videa-components/src/ui/button';
import { Progress } from '@repo/videa-components/src/ui/progress';
import { useAtom } from 'jotai';
import { useHistory } from 'react-router';
import { Redirect } from 'react-router-dom';

import routesConstants from '@/routes/routes.constants';

import { batchSessionAtom } from './batchSession';

export const BatchSummaryPage = () => {
  const history = useHistory();
  const [batchSession, setBatchSession] = useAtom(batchSessionAtom);

  const resultValues = useMemo(
    () => Object.values(batchSession?.results ?? {}),
    [batchSession?.results],
  );

  const minutesSpent = useMemo(() => {
    if (!batchSession?.startedAt) return 0;
    const diffMs = Date.now() - batchSession.startedAt;
    return Math.max(1, Math.round(diffMs / 60000));
  }, [batchSession?.startedAt]);

  if (!batchSession) {
    return <Redirect to={routesConstants.CLAIMS} />;
  }

  const pushedCount = resultValues.length;
  const totalAttachments = batchSession.patients.length;
  const skippedCount = totalAttachments - pushedCount;
  const reviewedCount = pushedCount;
  const progressValue =
    totalAttachments > 0 ? (pushedCount / totalAttachments) * 100 : 0;

  const minutesSuffix = minutesSpent !== 1 ? 's' : '';

  const handleBackToPatientList = () => {
    setBatchSession(null);
    history.push(routesConstants.CLAIMS);
  };

  return (
    <div className="vai-flex vai-flex-col vai-items-center vai-justify-center vai-h-full vai-bg-slate-50 vai-relative vai-overflow-hidden">
      {/* Summary Card */}
      <div
        className="vai-bg-white vai-rounded-2xl vai-shadow-lg vai-px-8 vai-text-center vai-relative vai-z-10 vai-border vai-border-gray-200 vai-bg-no-repeat vai-bg-top"
        style={{
          backgroundImage: 'url(/assets/images/confetti-background.svg)',
          width: '714px',
          height: '580px',
          backgroundSize: '714px 290px',
        }}
      >
        {/* Top half — 290px, confetti background */}
        <div className="vai-flex vai-flex-col vai-justify-center vai-items-center vai-h-[290px]">
          <h2 className="vai-text-2xl vai-font-semibold vai-text-foreground vai-mb-[62px]">
            <Trans>
              You reviewed {reviewedCount} of {totalAttachments} attachments in{' '}
              {minutesSpent} minute{minutesSuffix}
            </Trans>
          </h2>

          <Button
            onClick={handleBackToPatientList}
            className="vai-px-6 vai-py-2"
          >
            <Trans>Back to Patient List</Trans>
          </Button>
        </div>

        {/* Divider */}
        <div className="vai-border-t vai-border-gray-200 vai--mx-8" />

        {/* Bottom half — 290px */}
        <div className="vai-flex vai-flex-col vai-justify-center vai-h-[290px] vai-px-8">
          <div className="vai-flex vai-flex-col vai-text-left">
            {/* Title */}
            <p className="vai-text-sm vai-text-gray-500 vai-uppercase vai-tracking-wide vai-mb-6">
              <Trans>Review Summary</Trans>
            </p>

            {/* Progress bar */}
            <Progress
              value={progressValue}
              className="vai-h-8 vai-mb-10 vai-bg-gray-200 vai-rounded-lg"
              indicatorClassName="vai-bg-green-400 vai-rounded-lg"
            />

            {/* Legend */}
            <div className="vai-flex vai-flex-col vai-gap-6">
              <div className="vai-flex vai-items-center vai-justify-between">
                <div className="vai-flex vai-items-start vai-gap-2">
                  <div className="vai-flex vai-items-center vai-h-[1.75rem] vai-shrink-0">
                    <div className="vai-w-4 vai-h-4 vai-rounded vai-bg-green-400" />
                  </div>
                  <div className="vai-flex vai-flex-col vai-gap-0">
                    <span className="vai-text-lg vai-text-gray-700">
                      <Trans>Approved</Trans>
                    </span>
                    <span className="vai-text-sm vai-text-muted-foreground">
                      <Trans>(pushed to PMS, downloaded, printed)</Trans>
                    </span>
                  </div>
                </div>
                <span className="vai-text-lg vai-text-gray-500">
                  {pushedCount} <Trans>attachments</Trans>
                </span>
              </div>

              <div className="vai-flex vai-items-center vai-justify-between">
                <div className="vai-flex vai-items-center vai-gap-2">
                  <div className="vai-w-4 vai-h-4 vai-rounded vai-bg-gray-300" />
                  <span className="vai-text-lg vai-text-gray-700">
                    <Trans>Skipped</Trans>
                  </span>
                </div>
                <span className="vai-text-lg vai-text-gray-500">
                  {skippedCount} <Trans>attachments</Trans>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
